/**
 * Semantic tokens — same roles/names as before, Snowflake Stellar values.
 */

import { neutral } from './palette';

/** Colors for a single surface state (background, text, border). Used for both actionable and informational token variants. */
export interface SurfaceState {
  background: string;
  foreground: string;
  border: string;
}

/** Token set for interactive components (e.g. buttons) */
export interface ActionableTokens {
  default: SurfaceState;
  active: SurfaceState;
  disabled: SurfaceState;
  focusRing: string;
}

/** Token set for non-interactive messaging (alerts, badges, etc.) */
export interface InformationalTokens {
  default: SurfaceState;
  info: SurfaceState;
  success: SurfaceState;
  warning: SurfaceState;
  danger: SurfaceState;
}

/** Side navigation item and chrome colors. */
export interface SidenavTokens {
  activeBackground: string;
  activeBackgroundHover: string;
  activeForeground: string;
  hoverBackground: string;
  defaultForeground: string;
  subtleForeground: string;
  border: string;
  activeBorder: string;
  logoBrand: string;
  profileName: string;
}

/** Root theme: maps palette colors to semantic roles (brand, text, background, border, spacing, actionable, informational). */
export interface SemanticTokens {
  brand: string;
  text: {
    regular: string;
    recessive: string;
  };
  background: {
    page: string;
    paper: string;
    surface: string;
  };
  border: {
    subtle: string;
  };
  sidenav: SidenavTokens;
  spacing: {
    radius: {
      big: number;
      regular: number;
      small: number;
    };
    focusRing: number;
  };
  actionable: {
    primary: ActionableTokens;
    secondary: ActionableTokens;
    danger: ActionableTokens;
    tertiary: ActionableTokens;
  };
  informational: InformationalTokens;
}

export const semanticLight: SemanticTokens = {
  brand: '#276ee1',
  text: {
    regular: '#23272c',
    recessive: '#626c7e'
  },
  background: {
    page: neutral[100],
    paper: '#ffffff',
    // Opaque fill for chips/rows on paper. Avoid low-alpha rgba — MUI css
    // vars can drop the alpha and yield an unreadable solid color.
    surface: neutral[200]
  },
  border: {
    subtle: 'rgba(5, 25, 58, 0.16)'
  },
  sidenav: {
    activeBackground: '#e3efff',
    activeBackgroundHover: '#c4dcff',
    activeForeground: '#276ee1',
    hoverBackground: 'rgba(5, 25, 58, 0.16)',
    defaultForeground: '#23272c',
    subtleForeground: '#626c7e',
    border: 'rgba(5, 25, 58, 0.16)',
    activeBorder: '#276ee1',
    logoBrand: '#00A1D9',
    profileName: '#979fab'
  },
  spacing: {
    radius: {
      big: 24,
      regular: 16,
      small: 0
    },
    focusRing: 4
  },
  actionable: {
    primary: {
      default: {
        background: '#276ee1',
        foreground: '#ffffff',
        border: '#276ee1'
      },
      active: {
        background: '#234f99',
        foreground: '#ffffff',
        border: '#234f99'
      },
      disabled: {
        background: 'rgba(5, 25, 58, 0.07)',
        foreground: '#626c7e',
        border: 'rgba(5, 25, 58, 0.16)'
      },
      focusRing: '#3783ff'
    },
    secondary: {
      default: {
        background: '#ffffff',
        foreground: '#23272c',
        border: 'rgba(5, 25, 58, 0.16)'
      },
      active: {
        background: '#ffffff',
        foreground: '#626c7e',
        border: '#86909f'
      },
      disabled: {
        background: '#ffffff',
        foreground: '#626c7e',
        border: 'rgba(5, 25, 58, 0.16)'
      },
      focusRing: 'rgba(5, 25, 58, 0.16)'
    },
    danger: {
      default: {
        background: '#ffffff',
        foreground: '#be3122',
        border: '#e94937'
      },
      active: {
        background: '#ffffff',
        foreground: '#be3122',
        border: '#e94937'
      },
      disabled: {
        background: '#ffffff',
        foreground: '#626c7e',
        border: 'rgba(5, 25, 58, 0.16)'
      },
      focusRing: '#ffe8e3'
    },
    tertiary: {
      default: {
        background: '#ffffff',
        foreground: '#626c7e',
        border: 'transparent'
      },
      active: {
        background: 'rgba(5, 25, 58, 0.16)',
        foreground: '#23272c',
        border: 'transparent'
      },
      disabled: {
        background: 'transparent',
        foreground: '#626c7e',
        border: 'transparent'
      },
      focusRing: 'rgba(5, 25, 58, 0.16)'
    }
  },
  informational: {
    default: {
      background: '#ffffff',
      foreground: '#626c7e',
      border: 'rgba(5, 25, 58, 0.16)'
    },
    info: {
      background: '#e3efff',
      foreground: '#2365d1',
      border: '#3783ff'
    },
    success: {
      background: '#cefae9',
      foreground: '#04785d',
      border: '#00a67a'
    },
    warning: {
      background: '#ffecc9',
      foreground: '#8a6000',
      border: '#b87c00'
    },
    danger: {
      background: '#ffe8e3',
      foreground: '#be3122',
      border: '#e94937'
    }
  }
};

export const semanticDark: SemanticTokens = {
  brand: '#276ee1',
  text: {
    regular: '#d7dbe1',
    recessive: '#88909c'
  },
  background: {
    page: '#16181d',
    paper: '#1e2228',
    // Opaque raised fill on dark paper (same alpha-strip pitfall as light).
    surface: '#2a3039'
  },
  border: {
    subtle: 'rgba(215, 219, 225, 0.14)'
  },
  sidenav: {
    activeBackground: '#222e42',
    activeBackgroundHover: '#283b5a',
    activeForeground: '#6fa4fc',
    hoverBackground: 'rgba(215, 219, 225, 0.1)',
    defaultForeground: '#d7dbe1',
    subtleForeground: '#88909c',
    border: 'rgba(215, 219, 225, 0.14)',
    activeBorder: '#276ee1',
    logoBrand: '#00A1D9',
    profileName: '#979fab'
  },
  spacing: {
    radius: {
      big: 24,
      regular: 16,
      small: 8
    },
    focusRing: 4
  },
  actionable: {
    primary: {
      default: {
        background: '#276ee1',
        foreground: '#ffffff',
        border: '#276ee1'
      },
      active: {
        background: '#234f99',
        foreground: '#ffffff',
        border: '#234f99'
      },
      disabled: {
        background: 'rgba(215, 219, 225, 0.07)',
        foreground: '#88909c',
        border: 'rgba(215, 219, 225, 0.14)'
      },
      focusRing: '#3783ff'
    },
    secondary: {
      default: {
        background: '#1e2228',
        foreground: '#d7dbe1',
        border: 'rgba(215, 219, 225, 0.14)'
      },
      active: {
        background: '#1e2228',
        foreground: '#88909c',
        border: '#6b727e'
      },
      disabled: {
        background: '#1e2228',
        foreground: '#88909c',
        border: 'rgba(215, 219, 225, 0.14)'
      },
      focusRing: 'rgba(215, 219, 225, 0.14)'
    },
    danger: {
      default: {
        background: '#1e2228',
        foreground: '#ffa696',
        border: '#e94937'
      },
      active: {
        background: '#1e2228',
        foreground: '#ffa696',
        border: '#e94937'
      },
      disabled: {
        background: '#1e2228',
        foreground: '#88909c',
        border: 'rgba(215, 219, 225, 0.14)'
      },
      focusRing: '#362320'
    },
    tertiary: {
      default: {
        background: 'rgba(215, 219, 225, 0.04)',
        foreground: '#88909c',
        border: 'transparent'
      },
      active: {
        background: 'rgba(215, 219, 225, 0.1)',
        foreground: '#d7dbe1',
        border: 'transparent'
      },
      disabled: {
        background: 'transparent',
        foreground: '#88909c',
        border: 'transparent'
      },
      focusRing: 'rgba(215, 219, 225, 0.14)'
    }
  },
  informational: {
    default: {
      background: '#1e2228',
      foreground: '#88909c',
      border: 'rgba(215, 219, 225, 0.14)'
    },
    info: {
      background: '#202938',
      foreground: '#96c5ff',
      border: '#3783ff'
    },
    success: {
      background: '#122f26',
      foreground: '#82c8b0',
      border: '#00a67a'
    },
    warning: {
      background: '#332710',
      foreground: '#e0bf89',
      border: '#b87c00'
    },
    danger: {
      background: '#362320',
      foreground: '#ffa696',
      border: '#e94937'
    }
  }
};
